import { useMemo } from 'react';
import { useAppSelector } from 'hooks/typedHooks';
import { selectMerchantCustomizations } from 'containers/app/appSlice';
import { FSMPlan, FSMPlanStatus, FSM_ACTIVE_PLAN_STATES } from 'containers/integrations/fsm-integration/constants';
import DateUtils from 'utils/dateUtils';
import { getAmountWithCurrency } from 'utils/amountUtils';

interface PlanStates {
  isFreePlan: boolean;
  isPaidPlan: boolean;
  isNoPlan: boolean;
  isCancelledPlan: boolean;
  isTrialPlan: boolean;
  isPlanActive: boolean;
}

interface PlanStatus {
  isTrialEnded: boolean;
  isUserCancelled: boolean;
  isSystemDowngraded: boolean;
  hasDowngradedAfterTrial: boolean;
  hasPaymentFailed: boolean;
  isSubscriptionConfirmed: boolean;
  isPaymentMethodUpdated: boolean | undefined;
}

interface DateInfo {
  fsmPlanValidUntilDate: string;
  gracePeriodEndsAtDate: string;
  trialPeriodEndsAtDate: string;
}

interface FSMPlanInfo extends PlanStates, PlanStatus, DateInfo {
  currentFSMPlan: FSMPlan | undefined;
  status: FSMPlanStatus | undefined;
  fsmPlanValidUntil: string | undefined;
  gracePeriodEndsAt: string | undefined;
  trialPeriodEndsAt: string | undefined;
  trialDuration: number;
  price?: string;
  subscriptionPrice: string;
  showLiteText: boolean;
  freePlanFrom: string;
}

const useFSMPlanInfo = (): FSMPlanInfo => {
  const { subscriptionActivity } = useAppSelector(selectMerchantCustomizations);
  const {
    currentFSMPlan,
    fsmPlanValidUntil,
    isPaymentMethodUpdated,
    status,
    gracePeriodEndsAt,
    trialPeriodEndsAt,
    isTrialEnded,
    isUserCancelled,
    isSubscriptionConfirmed,
    trialDuration,
    price,
    freePlanFrom
  } = subscriptionActivity ?? {};

  const formatDate = (dateString: string | undefined): string => {
    return dateString ? DateUtils.getDateInFormat({ date: new Date(dateString), dateFormat: 'MMM dd, yyyy' }) : '';
  };

  const planStates: PlanStates = useMemo(
    () => ({
      isFreePlan: currentFSMPlan === FSMPlan.FREE,
      isPaidPlan: currentFSMPlan === FSMPlan.PAID,
      isNoPlan: currentFSMPlan === FSMPlan.NO_PLAN,
      isCancelledPlan: currentFSMPlan === FSMPlan.CANCELLED,
      isTrialPlan: currentFSMPlan === FSMPlan.TRIAL,
      isPlanActive: FSM_ACTIVE_PLAN_STATES.includes(currentFSMPlan)
    }),
    [currentFSMPlan]
  );

  const planStatus: PlanStatus = useMemo(
    () => ({
      isTrialEnded: isTrialEnded ?? false,
      isUserCancelled: isUserCancelled ?? false,
      isSystemDowngraded: status === FSMPlanStatus.SYSTEM_DOWNGRADE,
      hasDowngradedAfterTrial: status === FSMPlanStatus.TRIAL_ENDED,
      hasPaymentFailed: status === FSMPlanStatus.PAYMENT_FAILED,
      isSubscriptionConfirmed: isSubscriptionConfirmed ?? false,
      isPaymentMethodUpdated
    }),
    [status, isTrialEnded, isUserCancelled, isSubscriptionConfirmed, isPaymentMethodUpdated]
  );

  const dateInfo: DateInfo = useMemo(() => {
    return {
      fsmPlanValidUntilDate: formatDate(fsmPlanValidUntil),
      gracePeriodEndsAtDate: formatDate(gracePeriodEndsAt),
      trialPeriodEndsAtDate: formatDate(trialPeriodEndsAt)
    };
  }, [fsmPlanValidUntil, gracePeriodEndsAt, trialPeriodEndsAt]);

  const subscriptionPrice = useMemo(() => {
    return price ? getAmountWithCurrency(price) : '$99.99';
  }, [price]);

  const showLiteText = planStates.isFreePlan || planStates.isNoPlan;

  return {
    currentFSMPlan,
    status,
    fsmPlanValidUntil,
    gracePeriodEndsAt,
    trialPeriodEndsAt,
    trialDuration: trialDuration ?? 0,
    price,
    subscriptionPrice,
    showLiteText,
    freePlanFrom,
    ...planStates,
    ...planStatus,
    ...dateInfo
  };
};

export default useFSMPlanInfo;
